'use_strict';

import Mustache from 'mustache';

/**
 * Animations for location prompt
 */
const animations = {
  slideUp(el, delay?) {
    el.style.display = '';
    const animation = el.animate({ transform: ['translateY(0%)', 'translateY(-100%)'] }, delay || 200);
    animation.onfinish = () => {
      el.style.display = 'none';
    };
    return animation;
  },

  slideDown(el, delay?) {
    const animation = el.animate({ transform: ['translateY(-100%)', 'translateY(0px)'] }, delay || 200);
    return animation;
  },
};

/**
 * Partition function. Divides array into two based on a filter function.
 *
 * @param {array} array - Array to divide
 * @param {function} filter - Filtering function
 *
 * @returns {array<array>}
 */
function partition(array, filter) {
  let pass: any[] = [], fail: any[] = [];
  array.forEach((e, idx, arr) => (filter(e, idx, arr) ? pass : fail).push(e));
  return [pass, fail];
}

function isAdmin() {
  return document.body.classList.contains('view-admin-editor');
}

let locationPrompts: LocationPrompt = {

  /**
   * Redirect to another page
   * @param {string} address - url to redirect to
   * @param {boolean} matchPage - Check if current page exists in target site language. Otherwise redirects to target url in db (usually homepage).
   *
   * @returns {void}
   */
  redirect: function (address, matchPage = false) {
    let targetAddress = address;

    if (matchPage) {
      const targetAddressHost = new URL(address).host;
      const langVersionUrls = Array.from(document.querySelectorAll('link[rel="alternate"]')).map(el => (el as HTMLLinkElement).href);
      const matchedPageUrl = langVersionUrls.find(url => new URL(url).host === targetAddressHost);

      if (matchedPageUrl) {
        targetAddress = matchedPageUrl;
      }

      // NTCONT-1396: No redirect on news article page.
      if (document.location.pathname.indexOf('/about-us/news-article/') === 0) {
        return;
      }
    }

    document.location.replace(targetAddress);
  },

  /**
   * Hide the prompt
   * @param {boolean} chosenValue - true or false
   * @param {object} rule - Object with prompt data
   *
   * @returns {void}
   */
  hideCountryPrompt: function (chosenValue, rule) {
    let locationPrompt = document.getElementById('locationPrompt');
    document.querySelector(':root')?.classList.toggle('locationPromptOpen', false);
    animations.slideUp(locationPrompt);
    locationPrompt?.blur();
    locationPrompt?.setAttribute('aria-hidden', 'true');
    if (window.sessionStorage) {
      sessionStorage.setItem('prompt_' + rule.uuid, chosenValue.toString());
      if (rule.analytics_id) {
        localStorage.setItem('prompt_' + rule.uuid, chosenValue.toString());
        localStorage.setItem('campaign_popover_click_' + rule.uuid, (chosenValue ? '1' : '0'));
        sessionStorage.setItem('campaign_popover_click', (chosenValue ? '1' : '0'));
      }
    }
    if (chosenValue) {
      setTimeout(function () {
        locationPrompts.redirect(rule.redirection_target);
      }, 500);
    }
  },

  /**
   * Show the prompt
   * @param {object} rule - Object with prompt data
   *
   * @returns {void}
   */
  showCountryPrompt: function (rule) {

    let locationPrompt = document.getElementById('locationPrompt');

    if (!locationPrompt) {

      locationPrompt = locationPrompts.createPromptElement(rule)

      locationPrompt.setAttribute('aria-hidden', 'true');

      document.body.insertBefore(locationPrompt, document.body.firstChild);

      document.querySelector('a.yes')?.addEventListener('click', function (e) {
        e.preventDefault();

        /* send analytics event for clicking the prompt for redirect */
        if (rule.analytics_id) {
          locationPrompts.sendToAnalytics('campaign_popover_click', false, { campaign_popover_id: rule.analytics_id });
        } else {
          locationPrompts.sendToAnalytics('geo_redirect_click', rule.redirection_target);
        }

        locationPrompts.hideCountryPrompt(true, rule);
      });

      document.querySelector('a.no')?.addEventListener('click', function (e) {
        e.preventDefault();
        locationPrompts.hideCountryPrompt(false, rule);
      });

      document.querySelector(':root')?.classList.toggle('locationPromptOpen', true);


      locationPrompt.classList.toggle('locationPromptOpen', true);
      locationPrompt.blur();
      locationPrompt.setAttribute('aria-hidden', 'false');
      animations.slideDown(locationPrompt);
    }

  },

  /**
   * Send event to Google Analytics
   * @param {string} eventName - name of the event
   * @param {string} target - target url
   * @param {object} optional - optional event properties
  */
  sendToAnalytics: function (eventName, target, optional) {

    if (!isAdmin()) {
      window.dataLayer = window.dataLayer || []; // Not in admin > Push event to dataLayer
      let dataObject: {
        event: string,
        geo_from?: string,
        geo_to?: string,
      } = {
        'event': eventName,
      }

      if (typeof target === 'string') {
        dataObject.geo_from = window.location.origin;
        dataObject.geo_to = target;
      }

      if (optional) dataObject = { ...dataObject, ...optional };

      window.dataLayer.push(dataObject);
    }

  },

  /**
   * Create a div with id locationPrompt
   * add svg icon, text and buttons
   * @param {object} rule - Object with prompt data
   * @returns {object} - locationPrompt element
   */
  createPromptElement: function (rule) {
    let locationPrompt = document.createElement('div');
    locationPrompt.setAttribute('id', 'locationPrompt');
    locationPrompt.innerHTML = Mustache.render(window.location_prompts!.template, rule);
    return locationPrompt
  },

  /**
   * Check redirection rules. These might have automatic redirects or manual prompt
   * @param location
   * @param rules
   * @returns {boolean}
   */

  checkRedirectRules(location: UserLocation, rules) {

    const [automaticRules, promptRules] = partition(rules, rule => rule.automatic_redirect === true);

    // Check automatic redirects first
    // They are not active in admin & editor views, unless testing with ?runRedirects=1 queryparam
    const isAdminView = isAdmin();
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get('runRedirects');

    if (!isAdminView || (isAdminView && testParam)) {
      for (const automaticRule of automaticRules) {
        let rule = automaticRule as LocationRule;
        const countryCodes = rule.location_codes ? rule.location_codes.split(',') : [];
        const subdivisionCodes = rule.subdivision_codes ? rule.subdivision_codes.split(',') : [];

        // Do redirect only once. Has this redirect been performed before during this session?
        const item = window.sessionStorage ? sessionStorage.getItem('prompt_auto_' + rule.uuid) : false;

        if (!item) {
          if (rule.rule === 'inside_country_inside_subdivision') {
            if (countryCodes.length === 0 || subdivisionCodes.length === 0) {
              // Invalid rule, cannot match both if both not set. Skip this rule.
              console.log('[LocationPrompts] Skipping invalid rule: ' + rule.uuid);
              continue;
            }

            if (countryCodes.includes(location.countryCode || '') && subdivisionCodes.includes(location.subdivisionCode || '')) {
              // Match, redirect
              if (window.sessionStorage) {
                sessionStorage.setItem('prompt_auto_' + rule.uuid, 'true');
              }
              locationPrompts.redirect(rule.redirection_target, true);
              return true;
            }
          } else if (rule.rule === 'inside_country_outside_subdivision') {
            if (countryCodes.length === 0 || subdivisionCodes.length === 0) {
              // Invalid rule, cannot match both if both not set. Skip this rule.
              console.log('[LocationPrompts] Skipping invalid rule: ' + rule.uuid);
              continue;
            }

            if (countryCodes.includes(location.countryCode || '') && !subdivisionCodes.includes(location.subdivisionCode || '')) {
              // Match, redirect
              if (window.sessionStorage) {
                sessionStorage.setItem('prompt_auto_' + rule.uuid, 'true');
              }
              locationPrompts.redirect(rule.redirection_target, true);
              return true;
            }
          } else if (rule.rule === 'inside_country') {
            if (countryCodes.includes(location.countryCode || '')) {
              if (window.sessionStorage) {
                sessionStorage.setItem('prompt_auto_' + rule.uuid, 'true');
              }
              locationPrompts.redirect(rule.redirection_target, true);
              return true;
            }
          } else if (rule.rule === 'outside_country') {
            if (!countryCodes.includes(location.countryCode || '')) {
              if (window.sessionStorage) {
                sessionStorage.setItem('prompt_auto_' + rule.uuid, 'true');
              }
              locationPrompts.redirect(rule.redirection_target, true);
              return true;
            }
          }
        }
      }
    }

    // Normal prompts for other rules
    for (let i = 0; i < promptRules.length; i++) {

      const rule = rules[i];
      const location_codes = rule.location_codes ? rule.location_codes.split(',') : null;

      // has this prompt been answered?
      const item = window.sessionStorage ? sessionStorage.getItem('prompt_' + rule.uuid) : false; /* if sessionstorage is available, but item was not found, item becomes null */
      if (item || !location_codes) return false;

      // rule match
      if ((rule.rule === 'inside_country' && location_codes.indexOf(location.countryCode || '') !== -1) ||
        (rule.rule === 'outside_country' && location_codes.indexOf(location.countryCode || '') === -1)) {

        setTimeout(function () {
          locationPrompts.showCountryPrompt(rule);

          /* send analytics event for showing the prompt */
          locationPrompts.sendToAnalytics('geo_redirect_impression', rule.redirection_target);
        }, 1000);

        return true;
      }
    }

    return false;
  },

  /**
   * Check campaign rules. These might have AB testing active, where prompt is only shown to certain amount of users.
   * Only show prompt once, compared to redirection where it is shown every time until closed or redirected.
   *
   * @param location
   * @param rules
   * @returns {boolean}
   */

  checkCampaignRules(location, rules) {
    for (let i = 0; i < rules.length; i++) {

      const rule = rules[i];
      const location_codes = rule.location_codes ? rule.location_codes.split(',') : null;

      // Edit: Campaign rules were changed to be wanted once per user, not per session. So use localStorage
      const item = localStorage.getItem('prompt_' + rule.uuid) || false;

      if (item && window.sessionStorage) {
        // We need to set the keys that AB test is looking for to localStorage, so that it can be checked on page load.
        // AB test isn't specified by campaign id, so they look for keys 'campaign_popover_show' and 'campaign_popover_click'
        sessionStorage.setItem('prompt_' + rule.uuid, false.toString());
        sessionStorage.setItem('campaign_popover_show', localStorage.getItem('campaign_popover_show_' + rule.uuid) || '0');
        sessionStorage.setItem('campaign_popover_click', localStorage.getItem('campaign_popover_click_' + rule.uuid) || '0');
      }

      if (item || !location_codes) return false;

      // Randomize for AB testing.
      let skipByABTest = false;
      const random = Math.random() * 100;
      // AB_test_ratio should be a string 1-100. If faulty format or missing, show prompt to all
      if (!isNaN(rule.ab_test_ratio as unknown as number) ? random >= parseInt(rule.ab_test_ratio as unknown as string) : false) {
        skipByABTest = true;
      }

      // rule match
      if (window.location.pathname == "/" && (rule.rule === 'inside_country' && location_codes.indexOf(location.countryCode || '') !== -1) ||
        (rule.rule === 'outside_country' && location_codes.indexOf(location.countryCode || '') === -1)) {

        if (!skipByABTest) {
          setTimeout(function () {
            locationPrompts.showCountryPrompt(rule);
          }, 1000);
        }

        /* send analytics event for the prompt whether we show it or not */
        locationPrompts.sendToAnalytics(
          'campaign_popover_impression',
          false,
          {
            campaign_popover_id: rule.analytics_id,
            campaign_popover_show: skipByABTest ? 0 : 1
          }
        );

        // Store that this prompt has been shown, we only want to show it once
        localStorage.setItem('prompt_' + rule.uuid, false.toString());
        localStorage.setItem('campaign_popover_show_' + rule.uuid, (skipByABTest ? '0' : '1'));

        sessionStorage.setItem('prompt_' + rule.uuid, false.toString());
        sessionStorage.setItem('campaign_popover_show', (skipByABTest ? '0' : '1'));

        return !skipByABTest;
      }
    }

    return false;
  },

  /**
   * Check if there are any redirection rules that apply to the user
   * @param {Location} location - user's detected location
   * @returns {void}
   */
  checkRules: function (location = {}) {
    const rules = window.location_prompts?.rules || false;
    const campaignRules = window.location_prompts?.campaignRules || false;

    if (typeof location !== 'object' || !location.countryCode) {
      return;
    }

    let promptShown = false;

    // Redirection rules take precedence over campaign rules
    if (Array.isArray(rules)) {
      promptShown = this.checkRedirectRules(location, rules);
    }

    // Show campaign prompt only if no redirection prompt was shown
    if (!promptShown && Array.isArray(campaignRules)) {
      this.checkCampaignRules(location, campaignRules);
    }
  },

  /**
   * Initialize location prompts
   * @returns {void}
   */
  init: function () {
    // Check if there any redirection rules in page source
    if (!window.location_prompts) {
      return false; // no rules no worry
    }

    if (!window.sessionStorage) {
      return false; // no sessionstorage, no worry
    }

    // Get user's location from session storage.
    const userLocation = window.sessionStorage ? sessionStorage.getItem('user_location') : false;

    if (!userLocation) {
      const currentLanguage = document.documentElement.lang;

      /* fetch country code from server */
      fetch(`/stage-ajax/getCountryAndIP?currentLanguage=${currentLanguage}`, {
        method: 'get',
        cache: 'no-cache',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          // Store response to session storage. We don't want to query location service every time.
          sessionStorage.setItem('user_location', JSON.stringify(data));
          // At least countrycode must exist. Others optional.
          if (data?.countryCode) {
            locationPrompts.checkRules(data);
          }
        })
        .catch(function (error) {
          console.error(error);
        });

    }
    else if (userLocation) {
      let userLocationData;
      try {
        userLocationData = JSON.parse(userLocation);
      } catch (e) {
        userLocationData = false;
      }

      locationPrompts.checkRules(userLocationData);
    }
  }
};

interface LocationRule {
  redirection_target: string;
  rule: string;
  uuid: string;
  location_codes: string;
  subdivision_codes: string;
  yes_text: string;
  no_text: string;
  question: string;
}

interface RedirectRule extends LocationRule {
  automatic_redirect: boolean;
}

interface CampaignRule extends LocationRule {
  title?: string;
  ab_test_ratio?: string;
  analytics_id: string;
}

declare global {
  interface Window {
    location_prompts?: {
      rules: RedirectRule[];
      campaignRules: CampaignRule[];
      template: string;
    }
  }
}

interface UserLocation {
  countryCode?: string;
  subdivisionCode?: string;
}
interface LocationPrompt {
  redirect(address: string, matchPage?: boolean): void;
  hideCountryPrompt(chosenValue: boolean, rule: any): void;
  showCountryPrompt(rule: any): void;
  sendToAnalytics(eventName: string, target: string | boolean, optional?: object): void;
  createPromptElement(rule: any): HTMLElement;
  checkRules(location: UserLocation): void;
  checkRedirectRules(location: UserLocation, rules: RedirectRule[]): boolean;
  checkCampaignRules(location: UserLocation, rules: CampaignRule[]): boolean;
  init: () => void;
}

export default locationPrompts;
